import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ErrorProperties } from '../../API/identityAPIProperties';
import API from '../../API/Main/API';
import { resetState } from '../../resetState';
import {
	SaveUserDataProperties,
	UpdateUserDataProperties,
	UpdatedUserProperties,
	UserBranchesRequest,
	UserPaginationProperties,
	UserProperties,
	UserTableDataProperties,
} from '../../API/userAPIProperties';
import { PaginationProperties } from '../../../shared/uiComponents/Table/tableProperties';

export const getUsers = createAsyncThunk('/user/users', async (data: UserPaginationProperties, thunkAPI) => {
	const response = await API.UserAPI.User.getUsers(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as UserTableDataProperties;
});

export const getAdmins = createAsyncThunk('/user/users/admin', async (data: UserPaginationProperties, thunkAPI) => {
	const response = await API.UserAPI.User.getUsersBySection(data, 0);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as UserTableDataProperties;
});

export const getBCBAs = createAsyncThunk('/user/users/bcba', async (data: PaginationProperties, thunkAPI) => {
	const response = await API.UserAPI.User.getUsersBySection(data, 1);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as UserTableDataProperties;
});

export const getBTs = createAsyncThunk('/user/users/bt', async (data: PaginationProperties, thunkAPI) => {
	const response = await API.UserAPI.User.getUsersBySection(data, 2);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as UserTableDataProperties;
});

export const getClinicalSupervisors = createAsyncThunk(
	'/user/users/cs',
	async (data: PaginationProperties, thunkAPI) => {
		const response = await API.UserAPI.User.getUsersBySection(data, 3);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as UserTableDataProperties;
	},
);

export const addUser = createAsyncThunk('/user/add', async (data: SaveUserDataProperties, thunkAPI) => {
	const response = await API.UserAPI.User.addUser(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as UserProperties;
});

export const getUser = createAsyncThunk('/user/user', async (id: string, thunkAPI) => {
	const response = await API.UserAPI.User.getUser(id);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as UserProperties;
});

export const updateUser = createAsyncThunk('/user/update', async (data: UpdateUserDataProperties, thunkAPI) => {
	const response = await API.UserAPI.User.updateUser(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as UpdatedUserProperties;
});

export const deleteUser = createAsyncThunk('/user/delete', async (id: string, thunkAPI) => {
	const response = await API.UserAPI.User.deleteUser(id);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const saveUserBranches = createAsyncThunk(
	'user/user/save-branches',
	async (data: UserBranchesRequest, thunkAPI) => {
		const response = await API.UserAPI.User.saveUserBranches(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

interface UserInitialStateProperties {
	admins: UserTableDataProperties;
	bcbas: UserTableDataProperties;
	bts: UserTableDataProperties;
	clinicalSupervisors: UserTableDataProperties;
	error: ErrorProperties;
	isUpdating: boolean;
	loading: boolean;
	saveDisabled: boolean;
	user: UserProperties;
	users: UserTableDataProperties;
}

const initialUserState: UserInitialStateProperties = {
	loading: false,
	isUpdating: false,
	error: {
		status: 0,
		title: '',
	},
	users: {
		query: null,
		page: 1,
		totalNumberOfItems: 0,
		numberOfItems: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	},
	clinicalSupervisors: {
		query: null,
		page: 1,
		totalNumberOfItems: 0,
		numberOfItems: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	},
	admins: {
		query: null,
		page: 1,
		totalNumberOfItems: 0,
		numberOfItems: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	},
	bcbas: {
		query: null,
		page: 1,
		totalNumberOfItems: 0,
		numberOfItems: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	},
	bts: {
		query: null,
		page: 1,
		totalNumberOfItems: 0,
		numberOfItems: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	},
	user: {
		id: '',
		firstName: '',
		lastName: '',
		fullName: '',
		patronymic: '',
		numberOfClients: 0,
		phoneNumber: '',
		address: '',
		notes: '',
		email: '',
		numberOfNotes: 0,
		role: {
			id: '',
			name: '',
			section: {
				id: 0,
				name: '',
			},
		},
		createdBy: {
			id: '',
			fullName: '',
			role: '',
			authorizationType: {
				id: 0,
				name: '',
			},
		},
		authorizationType: {
			id: 0,
			name: '',
		},
		emailConfirmed: false,
		isDeactivated: false,
		isCaseCoordinator: false,
		mbhId: '',
		caseCoordinators: [],
	},
	saveDisabled: false,
};

const userSlice = createSlice({
	name: 'user',
	initialState: initialUserState,
	reducers: {
		updateSaveDisabled(state, action) {
			state.saveDisabled = action.payload;
		},
	},
	extraReducers: (builder) => {
		// get users
		builder.addCase(getUsers.pending, (state) => {
			state.loading = true;
			state.error = initialUserState.error;
		});
		builder.addCase(getUsers.fulfilled, (state, action) => {
			state.loading = false;
			state.users = action.payload;
		});
		builder.addCase(getUsers.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// get admin
		builder.addCase(getAdmins.pending, (state) => {
			state.loading = true;
			state.error = initialUserState.error;
		});
		builder.addCase(getAdmins.fulfilled, (state, action) => {
			state.loading = false;
			state.admins = action.payload;
		});
		builder.addCase(getAdmins.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// get BCBA
		builder.addCase(getBCBAs.pending, (state) => {
			// state.loading = true;
			state.error = initialUserState.error;
		});
		builder.addCase(getBCBAs.fulfilled, (state, action) => {
			// state.loading = false;
			state.bcbas = action.payload;
		});
		builder.addCase(getBCBAs.rejected, (state, action) => {
			// state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getBTs
		builder.addCase(getBTs.pending, (state) => {
			state.loading = true;
			state.error = initialUserState.error;
		});
		builder.addCase(getBTs.fulfilled, (state, action) => {
			state.loading = false;
			state.bts = action.payload;
		});
		builder.addCase(getBTs.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getClinicalSsupervisors
		builder.addCase(getClinicalSupervisors.pending, (state) => {
			// state.loading = true;
			state.error = initialUserState.error;
		});
		builder.addCase(getClinicalSupervisors.fulfilled, (state, action) => {
			// state.loading = false;
			state.clinicalSupervisors = action.payload;
		});
		builder.addCase(getClinicalSupervisors.rejected, (state, action) => {
			// state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// get user
		builder.addCase(getUser.pending, (state) => {
			state.loading = true;
			state.error = initialUserState.error;
		});
		builder.addCase(getUser.fulfilled, (state, action) => {
			state.loading = false;
			state.user = action.payload;
		});
		builder.addCase(getUser.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// add user
		builder.addCase(addUser.pending, (state) => {
			state.loading = true;
			state.error = initialUserState.error;
		});
		builder.addCase(addUser.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(addUser.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// update user
		builder.addCase(updateUser.pending, (state) => {
			state.isUpdating = true;
			state.error = initialUserState.error;
		});
		builder.addCase(updateUser.fulfilled, (state) => {
			state.isUpdating = false;
		});
		builder.addCase(updateUser.rejected, (state, action) => {
			state.isUpdating = false;
			state.error = action.payload as ErrorProperties;
		});
		// delete user
		builder.addCase(deleteUser.pending, (state) => {
			state.loading = true;
			state.error = initialUserState.error;
		});
		builder.addCase(deleteUser.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(deleteUser.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// saveUserBranches
		builder.addCase(saveUserBranches.pending, (state) => {
			state.loading = true;
			state.error = initialUserState.error;
		});
		builder.addCase(saveUserBranches.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(saveUserBranches.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// reset state
		builder.addCase(resetState, () => initialUserState);
	},
});

export const { updateSaveDisabled } = userSlice.actions;
export default userSlice.reducer;
